import React, { useState } from 'react';
import type { MembershipFigures, MembershipHistoryChurch, MembershipHistoryDistrict } from '../types';
import { formatCount } from '../utils/membershipFormat';
import MembershipPhoneRow, { usePhone } from './MembershipPhoneRow';
import '../membership.css';

const COLUMNS: Array<{ key: keyof MembershipFigures; label: string }> = [
  { key: 'total_members', label: 'Total' },
  { key: 'male_count', label: 'Male' },
  { key: 'female_count', label: 'Female' },
  { key: 'added_count', label: 'Added' },
  { key: 'transferred_in_count', label: 'Transferred in' },
  { key: 'deceased_count', label: 'Deceased' },
  { key: 'transferred_out_count', label: 'Transferred out' },
  { key: 'excluded_count', label: 'Excluded' },
];

function cells(row: MembershipFigures) {
  return COLUMNS.map((column) => {
    const value = (row[column.key] as number | null | undefined) ?? 0;
    return (
      <td key={column.key} className={`text-end${value === 0 ? ' membership-zero' : ''}`}>
        {formatCount(value)}
      </td>
    );
  });
}

function NameCell({ label, level }: { label: string; level: number }) {
  return (
    <td className={`membership-history__first membership-history__level-${level}`}>
      <span className="membership-history__spacer" aria-hidden="true" />
      <span className="membership-history__name" title={label}>{label}</span>
    </td>
  );
}

interface Props {
  scope: 'DISTRICT' | 'NATION';
  churches?: MembershipHistoryChurch[];
  districts?: MembershipHistoryDistrict[];
}

/** Report period breakdown: the history table look without the month layer. Districts start collapsed. */
const MembershipBreakdownTable: React.FC<Props> = ({ scope, churches = [], districts = [] }) => {
  const [openDistricts, setOpenDistricts] = useState<Record<string, boolean>>({});
  const phone = usePhone();
  const empty = scope === 'DISTRICT' ? churches.length === 0 : districts.length === 0;

  if (empty) {
    return <p className="text-muted mb-0">No churches yet</p>;
  }

  if (phone) {
    return (
      <div className="membership-history membership-history--cards">
        <div className="membership-history-card membership-breakdown-card">
          {scope === 'DISTRICT'
            ? churches.map((church) => (
              <div key={church.church_name}><MembershipPhoneRow label={church.church_name} figures={church} /></div>
            ))
            : districts.map((district) => (
              <div key={district.district_name}><MembershipPhoneRow label={district.district_name} figures={district}>
                {district.churches.map((church) => (
                  <div key={church.church_name}><MembershipPhoneRow label={church.church_name} figures={church} nested /></div>
                ))}
              </MembershipPhoneRow></div>
            ))}
        </div>
      </div>
    );
  }

  const body: React.ReactNode[] = [];
  if (scope === 'DISTRICT') {
    for (const church of churches) {
      body.push(
        <tr key={church.church_name}>
          <NameCell label={church.church_name} level={1} />
          {cells(church)}
        </tr>
      );
    }
  } else {
    for (const [index, district] of districts.entries()) {
      const open = Boolean(openDistricts[district.district_name]);
      const controls = `breakdown-district-${index}`;
      body.push(
        <tr key={district.district_name} className="membership-history-district">
          <td className="membership-history__first membership-history__level-1">
            <button
              type="button"
              className="membership-caret membership-caret--row btn btn-link btn-sm p-0 text-start"
              aria-expanded={open}
              aria-controls={controls}
              onClick={() => setOpenDistricts((current) => ({ ...current, [district.district_name]: !current[district.district_name] }))}
            >
              <span className="membership-caret__mark" aria-hidden="true">{open ? '\u25BE' : '\u25B8'}</span>
              <span className="membership-history__name" title={district.district_name}>{district.district_name}</span>
            </button>
          </td>
          {cells(district)}
        </tr>
      );
      if (open) {
        for (const [churchIndex, church] of district.churches.entries()) {
          body.push(
            <tr key={`${district.district_name}-${church.church_name}`} id={churchIndex === 0 ? controls : undefined}>
              <NameCell label={church.church_name} level={2} />
              {cells(church)}
            </tr>
          );
        }
      }
    }
  }

  return (
    <div className="membership-history">
      <div className="membership-history__frame">
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0 membership-history__table">
            <thead>
              <tr>
                <th className="membership-history__first">{scope === 'DISTRICT' ? 'Church' : 'District'}</th>
                {COLUMNS.map((column) => (
                  <th key={column.key} className="text-end">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>{body}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MembershipBreakdownTable;
